import * as React from 'react';
import { styled, alpha } from '@mui/material/styles';
import Button, { ButtonProps } from '@mui/material/Button';
import Menu, { MenuProps } from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Badge, Chip } from '@mui/material';
import { Note, NoteOutlined } from '@mui/icons-material';

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    sx={{ borderRadius: '2px' }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 2,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color:
      theme.palette.mode === 'light'
        ? 'rgb(55, 65, 81)'
        : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity
        ),
      },
    },
  },
}));

export interface StageButtonMenuItemProps {
  label: string;
  icon?: JSX.Element;
  onClick: Function;
}

export type StageButtonMenuProps = Omit<ButtonProps, 'onClick'> & {
  options: any[];
  disabled?: boolean;
  label: string;
  onClick: Function;
  onInfo?: Function;
  onClear?: Function;
  color?: string; //"inherit" | "primary" | "secondary" | "success" | "error" | "info" | "warning" | undefined
};

export const StageButtonMenu = ({
  options,
  disabled = false,
  label: inLabel,
  onClick,
  onClear,
  onInfo,
  color,
  sx: buttonSx = {},
}: StageButtonMenuProps) => {
  const buttonRef = React.useRef<any>(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [label, setLabel] = React.useState<string>(inLabel);
  React.useEffect(() => setLabel(inLabel), [inLabel]);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(null);
  };

  const menuWidth = anchorEl && anchorEl.offsetWidth ? anchorEl.offsetWidth : 0;

  return (
    <div
      onClick={event => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      {onClear ? (
        <Chip
          icon={onInfo ? <NoteOutlined fontSize="small" /> : undefined}
          label={label}
          clickable={!!onInfo}
          onClick={() => {
            if (onInfo) {
              onInfo();
            }
          }}
          onDelete={() => onClear()}
          sx={{
            boxShadow: theme => theme.shadows[2],
            border: '1px solid black',
          }}
        />
      ) : (
        <>
          <Button
            ref={buttonRef}
            disabled={disabled}
            variant="outlined"
            onClick={handleClick}
            endIcon={<KeyboardArrowDownIcon />}
            sx={buttonSx}
          >
            {label}
          </Button>
          <StyledMenu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            PaperProps={{
              sx: menuWidth ? { width: { xs: menuWidth, sm: 'auto' } } : {},
            }}
          >
            {options &&
              options
                ?.filter(o => o.name != label)
                ?.map((menuItem, index) => (
                  <MenuItem
                    key={index}
                    onClick={event => {
                      setLoading(true);
                      event.preventDefault();
                      event.stopPropagation();
                      // menuItem.onClick();
                      onClick(menuItem);
                      handleClose(event);
                      setLoading(false);
                      setLabel(menuItem.name);
                    }}
                    disableRipple
                  >
                    {menuItem.name}
                  </MenuItem>
                ))}
          </StyledMenu>
        </>
      )}
    </div>
  );
};
